import * as React from 'react'
import { ReactElement, useMemo, useRef } from 'react'
import { bundleIcon, MegaphoneLoudFilled, MegaphoneLoudRegular } from '@fluentui/react-icons'
import { Button, Tooltip } from '@fluentui/react-components'
import { useTranslation } from 'react-i18next'
import useAxios from '../../../hooks/useAxios'
import { TimeSuggestionFilterType, TimeSuggestionOverviewDto, TimeSuggestionStatus } from '../../../pages/Time/BaseTimeSuggestionOverview'
import useQueueDispatch from '../../../hooks/queue/useQueueDispatch'
import { isTimeSuggestionJob, QueueJobKind, timeSuggestionJobId } from '../../../hooks/queue/queueTypes'
import { AxiosResponse } from 'axios'
import useUser from '../../../hooks/useUser'
import { useActiveToastIds } from '../../../components/Toast/AppToaster'
import { parseFilterValuesToParams } from '../../../pages/BaseOverview/BaseOverview'
import { SortDirection } from '../../../context/GlobalContext'
import { useSettings } from '../../../hooks/useSettings'
import { DateTime } from 'luxon'

const Icon = bundleIcon(MegaphoneLoudFilled, MegaphoneLoudRegular)

export default function TimeSuggestionCounter(): ReactElement {
    const axios = useAxios()
    const { settings } = useSettings()
    const { t: translate } = useTranslation()
    const { currentUser } = useUser()
    const activeToastIds = useActiveToastIds()

    const dispatch = useQueueDispatch<unknown, TimeSuggestionOverviewDto>(QueueJobKind.TimeSuggestion)

    // Counts how often the user asked for the suggestions, see the job id below.
    const round = useRef(0)

    // Hands the suggestions to the queue, one job per suggestion, so each one gets its own toast.
    //
    // These are toast-only jobs: without a context the queue finishes them the moment they are pushed and the
    // QueueToaster takes them straight back off the queue, leaving just the toast behind. Dispatching them in one
    // pass keeps that whole cascade inside a single batched update. Feeding them to the queue one at a time through
    // state does not: every store notification then re-enters the effect that dispatches the next one, and React
    // counts that chain as nested updates until it gives up with "Maximum update depth exceeded" (HAM-4309).
    const show = (suggestions: TimeSuggestionOverviewDto[]): void =>
        // Reversed, so the oldest suggestion of the batch is shown first.
        [...suggestions]
            .reverse()
            // Skip whatever is already on screen, whichever round or route raised it. Pressing the megaphone
            // twice used to offer the same suggestion twice, and handling one of the two left the other behind to
            // be rejected by the server.
            .filter(suggestion => ![...activeToastIds].some(toastId => isTimeSuggestionJob(String(toastId), suggestion.id)))
            .forEach(suggestion =>
                // The job id doubles as the toast id, and Fluent silently ignores a toast whose id it has seen
                // before. Asking for the same suggestion a second time therefore has to arrive under a new id, or
                // nothing would show up.
                dispatch(timeSuggestionJobId(suggestion.id, round.current), undefined, suggestion),
            )

    const filterValues = useMemo<TimeSuggestionFilterType>(
        () => ({
            itemUsers: [currentUser?.id],
            statuses: [TimeSuggestionStatus.ToReview],
            from: settings?.timeSuggestionNotificationsDays
                ? DateTime.now().startOf('day').minus({ days: settings.timeSuggestionNotificationsDays }).toUTC().toISODate()
                : undefined,
        }),
        [settings, currentUser],
    )

    if (settings?.timeSuggestionNotificationsDays === 0) {
        return <></>
    }

    return (
        <Tooltip
            content={translate('time.suggestion.showlatest', { count: settings?.timeSuggestionNotificationsDays ?? 0 })}
            relationship={'label'}
            withArrow
        >
            <Button
                icon={<Icon />}
                appearance={'subtle'}
                onClick={() => {
                    round.current++
                    axios
                        .get<TimeSuggestionOverviewDto[]>('/time-suggestion', {
                            params: parseFilterValuesToParams(
                                filterValues,
                                { property: ['emailSent', 'id'], direction: SortDirection.Descending },
                                0,
                                5,
                            ),
                        })
                        .then((response: AxiosResponse<TimeSuggestionOverviewDto[]>) => show(response.data))
                }}
            />
        </Tooltip>
    )
}
